export const formatTime = (timestamp: number, hasMins: boolean): string => {
    const t = (window as any).__ReMoI18n.t;
    const todayDesc = t("today");
    const yesterdayDesc = t("yesterday");
    const dayBeforeYesterdayDesc = t("dayBeforeYesterday");
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dayBeforeYesterday = new Date(today);
    dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hour = date.getHours().toString().padStart(2, "0");
    const minute = date.getMinutes().toString().padStart(2, "0");
    const second = date.getSeconds().toString().padStart(2, "0");

    if (date >= today) {
        return hasMins ? `${todayDesc} ${hour}:${minute}:${second}` : todayDesc;
    } else if (date >= yesterday) {
        return hasMins ? `${yesterdayDesc} ${hour}:${minute}:${second}` : yesterdayDesc;
    } else if (date >= dayBeforeYesterday) {
        return hasMins
            ? `${dayBeforeYesterdayDesc} ${hour}:${minute}:${second}`
            : dayBeforeYesterdayDesc;
    } else {
        const yearStr = [year, month, day].join("-");
        const minsStr = [hour, minute, second].join(":");
        return hasMins ? yearStr + " " + minsStr : yearStr;
    }
};

/**
 * @param timestamp 发出时间
 * @param expireTime 过期时限n天
 * @returns 是否过期
 */
export const expire = (timestamp: number, expireTime: number): boolean => {
    const now = new Date();
    const expireDays = new Date(now);
    expireDays.setDate(expireDays.getDate() - expireTime);
    const deadTimestamp = expireDays.getTime();
    return timestamp > deadTimestamp;
};

interface SortableContact {
    isLastMessageRead?: boolean;
    lastMessageTime?: number;
    status?: string;
    userName?: string;
}

export const sortFriends = <T extends SortableContact>(friends: T[]): T[] => {
    return friends.sort((a, b) => {
        if (
            !a.isLastMessageRead &&
            a.lastMessageTime &&
            b.lastMessageTime &&
            a.lastMessageTime > b.lastMessageTime
        ) {
            return -1;
        }
        if (
            !a.isLastMessageRead &&
            a.lastMessageTime &&
            b.lastMessageTime &&
            a.lastMessageTime < b.lastMessageTime
        ) {
            return 1;
        }
        if (a.status === "online" && b.status !== "online") {
            return -1;
        }
        if (a.status !== "online" && b.status === "online") {
            return 1;
        }
        const nameA = a.userName || ""; // 处理 undefined 或 null 的情况
        const nameB = b.userName || "";

        const firstCharA = nameA.charAt(0);
        const firstCharB = nameB.charAt(0);

        const typeA = getCharTypePriority(firstCharA);
        const typeB = getCharTypePriority(firstCharB);

        // 1. 比较首字符类型优先级
        if (typeA !== typeB) {
            return typeA - typeB; // 优先级高的排在前面 (1 < 2 < 3)
        }
        if (typeA === 0) {
            return nameA.localeCompare(nameB, "zh-Hans-CN");
        } else if (typeA === 1) {
            // 2. 如果首字符类型相同，则根据具体类型进行二次排序
            // 都是字母
            // 使用 localeCompare 进行字母排序，忽略大小写
            return nameA.localeCompare(nameB, undefined, { sensitivity: "case" });
        } else if (typeA === 2 || typeA === 3) {
            // 都是数字
            // 转换为数字进行比较
            const numA = parseInt(firstCharA, 10);
            const numB = parseInt(firstCharB, 10);
            if (numA !== numB) {
                return numA - numB;
            }
            // 如果数字相同，再按完整字符串比较 (例如 '1stUser' 和 '10thUser')
            return nameA.localeCompare(nameB, undefined, { sensitivity: "case" });
        } else {
            // 都是其他字符
            // 其他情况（例如首字符都是特殊符号或空）
            return nameA.localeCompare(nameB);
        }
    });
};

// 定义一个辅助函数，获取字符类型和比较优先级
function getCharTypePriority(char: string): number {
    if (!char) {
        return 4; // 空字符优先级最低
    }
    const code = char.charCodeAt(0);
    // 判断是否是中文
    if (code >= 0x4e00 && code <= 0x9fff) {
        return 0; // 中文优先级最高
    }
    // 判断是否是大写字母 (A-Z)
    if (code >= 65 && code <= 90) {
        return 1;
    }
    // 判断是否是小写字母 (a-z)
    if (code >= 97 && code <= 122) {
        return 2;
    }
    // 判断是否是数字 (0-9)
    if (code >= 48 && code <= 57) {
        return 3;
    }
    return 4; // 其他字符优先级最低
}

export function formatShowTime(timestamp: number): string {
    const t = (window as any).__ReMoI18n.t;
    const todayDesc = t("today");
    const yesterdayDesc = t("yesterday");
    const now = new Date();
    const date = new Date(timestamp === 13 ? timestamp : timestamp * 1000);

    // 设置今天的开始时间 (午夜 00:00:00)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // 设置昨天的开始时间 (午夜 00:00:00)
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

    // 获取时间戳的小时和分钟，并判断上午/下午
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const AM_PM = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 === 0 ? 12 : hours % 12; // 12小时制，0点和12点显示为12

    // 格式化分钟，确保两位数
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;

    // 判断是否是今天
    if (date.getTime() >= today.getTime()) {
        return `${todayDesc} ${displayHours}:${formattedMinutes}${AM_PM}`;
    } else if (date.getTime() >= yesterday.getTime()) {
        // 判断是否是昨天
        return `${yesterdayDesc} ${displayHours}:${formattedMinutes}${AM_PM}`;
    } else {
        // 其他日期
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // 月份从0开始
        const day = date.getDate();
        return `${year}/${month}/${day} ${displayHours}:${formattedMinutes}${AM_PM}`;
    }
}
